// PIN pad — screen-local kiosk atom (NOT in the mockup: kiosk.jsx draws a
// card/QR scan surface, but the production device-auth model is spec 26's
// per-employee PIN, so this pad is designed in the novem language —
// mockup form fields' white/raised + border-2 accents, big touch targets).
// Candidate for packages/components if a second surface needs it
// (reported under missing_atoms).

import { Check, Delete } from "lucide-react";
import { useTranslation } from "react-i18next";

import { PIN_MAX_LENGTH } from "@/kiosk-machine";

export interface PinPadProps {
  readonly pin: string;
  readonly canSubmit: boolean;
  readonly disabled?: boolean;
  readonly onDigit: (digit: string) => void;
  readonly onBackspace: () => void;
  readonly onSubmit: () => void;
}

const DIGIT_ROWS: readonly (readonly string[])[] = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
];

const KEY_BASE =
  "flex h-[60px] w-[96px] items-center justify-center rounded-[18px] transition-colors sm:h-[72px] " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] " +
  "disabled:cursor-not-allowed disabled:opacity-50";

const DIGIT_KEY =
  `${KEY_BASE} border border-[var(--border-default)] bg-[var(--bg-raised)] ` +
  "font-mono text-[28px] font-medium text-[var(--fg-primary)] shadow-[var(--shadow-xs)] " +
  "active:bg-[var(--bg-subtle)]";

export function PinPad({
  pin,
  canSubmit,
  disabled = false,
  onDigit,
  onBackspace,
  onSubmit,
}: PinPadProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center gap-4 sm:gap-7">
      {/* Entered-digit dots */}
      <div
        role="status"
        aria-label={t("kiosk.pin.dots_label")}
        className="flex h-[18px] items-center gap-3.5"
      >
        {Array.from({ length: PIN_MAX_LENGTH }, (_, i) => (
          <span
            key={i}
            aria-hidden="true"
            className={`rounded-full transition-all ${
              i < pin.length
                ? "h-[14px] w-[14px] bg-[var(--color-accent-500)]"
                : "h-[10px] w-[10px] bg-[var(--border-default)]"
            }`}
          />
        ))}
      </div>

      {/* SR-only count announcer — the dots are aria-hidden, so this polite
          region is how non-sighted users feel the PIN fill (count only, never
          the digits themselves). */}
      <p aria-live="polite" className="sr-only">
        {t("kiosk.pin.digits_entered", { entered: pin.length, total: PIN_MAX_LENGTH })}
      </p>

      {/* Keys */}
      <div className="flex flex-col gap-2 sm:gap-3">
        {DIGIT_ROWS.map((row) => (
          <div key={row[0]} className="flex gap-3">
            {row.map((digit) => (
              <button
                key={digit}
                type="button"
                disabled={disabled}
                className={DIGIT_KEY}
                onClick={() => onDigit(digit)}
              >
                {digit}
              </button>
            ))}
          </div>
        ))}
        <div className="flex gap-3">
          <button
            type="button"
            aria-label={t("kiosk.pin.key_backspace")}
            disabled={disabled || pin.length === 0}
            className={`${KEY_BASE} border border-[var(--border-subtle)] bg-transparent text-[var(--fg-muted)] active:bg-[var(--bg-subtle)]`}
            onClick={onBackspace}
          >
            <Delete size={28} aria-hidden="true" />
          </button>
          <button type="button" disabled={disabled} className={DIGIT_KEY} onClick={() => onDigit("0")}>
            0
          </button>
          <button
            type="button"
            aria-label={t("kiosk.pin.continue")}
            disabled={disabled || !canSubmit}
            className={`${KEY_BASE} bg-[var(--accent)] text-[var(--fg-on-accent)] shadow-[var(--shadow-md)] active:bg-[var(--accent-active)]`}
            onClick={onSubmit}
          >
            <Check size={30} strokeWidth={2.4} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
